
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShortContent } from '@/hooks/useShortContent';
import { ShortContentGrid } from '@/components/short-content/ShortContentGrid';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Info, Lightbulb, Quote, Star, FileText, AlertTriangle, RefreshCw, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { TravelByteBreadcrumbs } from '@/components/travel-bytes/TravelByteBreadcrumbs';
import { TravelByteSearch } from '@/components/travel-bytes/TravelByteSearch';
import { TravelByteFilters, FilterState } from '@/components/travel-bytes/TravelByteFilters';
import { TravelBytePagination } from '@/components/travel-bytes/TravelBytePagination';
import { useIsMobile } from '@/hooks/use-mobile';

// Performance monitoring utilities
const performanceMonitor = {
  startTime: 0,
  
  start(operation: string) {
    this.startTime = performance.now();
    console.log(`🚀 Starting ${operation}`);
  },
  
  end(operation: string) {
    const duration = performance.now() - this.startTime;
    console.log(`✅ ${operation} completed in ${duration.toFixed(2)}ms`);
    
    if (duration > 1000) {
      console.warn(`⚠️ Slow operation detected: ${operation} took ${duration.toFixed(2)}ms`);
    }
    
    return duration;
  }
};

export default function ShortContentPage() {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [filters, setFilters] = useState<FilterState>({
    sortBy: 'date-desc'
  });
  const navigate = useNavigate();
  const baseUrl = window.location.origin;
  const isMobile = useIsMobile();
  
  console.log('ShortContentPage - Rendering with activeTab:', activeTab, 'page:', currentPage, 'filters:', filters);
  
  // Performance monitoring for initial load
  useEffect(() => {
    performanceMonitor.start('ShortContentPage initial load');
    return () => {
      performanceMonitor.end('ShortContentPage initial load');
    };
  }, []);
  
  // Memoized options to prevent unnecessary re-renders
  const featuredOptions = useMemo(() => ({ 
    featured: true, 
    limit: 3, 
    status: 'published' as const
  }), []);
  
  const regularOptions = useMemo(() => {
    const options: any = {
      contentType: activeTab !== 'all' ? activeTab as any : undefined,
      limit: 12,
      page: currentPage,
      status: 'published' as const
    };

    // Add filters
    if (filters.category) {
      options.category = filters.category;
    }

    // Add search to content type filter if both tab and search filter are different
    if (filters.contentType && filters.contentType !== activeTab) {
      options.contentType = filters.contentType;
    }

    return options;
  }, [activeTab, currentPage, filters.category, filters.contentType]);
  
  const featuredContent = useShortContent(featuredOptions);
  const regularContent = useShortContent(regularOptions);
  
  // Enhanced loading progress tracking
  useEffect(() => {
    const featuredLoading = featuredContent.isLoading;
    const regularLoading = regularContent.isLoading;
    
    if (featuredLoading && regularLoading) {
      setLoadingProgress(25);
    } else if (featuredLoading || regularLoading) {
      setLoadingProgress(75);
    } else {
      setLoadingProgress(100);
      setTimeout(() => setLoadingProgress(0), 500);
    }
  }, [featuredContent.isLoading, regularContent.isLoading]);
  
  // Get available categories and content types for filters
  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    [...featuredContent.items, ...regularContent.items].forEach(item => {
      if (item.category) categories.add(item.category);
    });
    return Array.from(categories).sort();
  }, [featuredContent.items, regularContent.items]);

  const contentTypes = useMemo(() => [
    { value: 'tip', label: 'Tips' },
    { value: 'fact', label: 'Facts' },
    { value: 'quote', label: 'Quotes' },
    { value: 'highlight', label: 'Highlights' },
    { value: 'snippet', label: 'Snippets' }
  ], []);

  // Filter and sort items based on current filters
  const filteredAndSortedItems = useMemo(() => {
    let items = [...regularContent.items];

    // Apply search filter
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      items = items.filter(item => 
        item.title.toLowerCase().includes(searchTerm) ||
        item.content.toLowerCase().includes(searchTerm) ||
        (item.category && item.category.toLowerCase().includes(searchTerm))
      );
    }

    // Apply sorting
    if (filters.sortBy) {
      items.sort((a, b) => {
        switch (filters.sortBy) {
          case 'date-desc':
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          case 'date-asc':
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          case 'title-asc':
            return a.title.localeCompare(b.title);
          case 'title-desc':
            return b.title.localeCompare(a.title);
          case 'views-desc':
            return b.view_count - a.view_count;
          default:
            return 0;
        }
      });
    }

    return items;
  }, [regularContent.items, filters.search, filters.sortBy]);
  
  const handleViewItem = useCallback((id: string) => {
    performanceMonitor.start('Navigate to content item');
    console.log('Viewing item with id:', id);
    regularContent.incrementViewCount(id);
    
    const item = regularContent.items.find(item => item.id === id);
    if (item && item.slug) {
      navigate(`/travel-bytes/${item.slug}`);
      performanceMonitor.end('Navigate to content item');
    } else {
      console.error('Could not find item with id:', id, 'or item has no slug');
      toast.error('Content not found', {
        description: 'The requested content could not be located. Please try again.'
      });
    }
  }, [regularContent, navigate]);
  
  const handleViewFeatured = useCallback((id: string) => {
    performanceMonitor.start('Navigate to featured content');
    console.log('Viewing featured item with id:', id);
    featuredContent.incrementViewCount(id);
    
    const item = featuredContent.items.find(item => item.id === id);
    if (item && item.slug) {
      navigate(`/travel-bytes/${item.slug}`);
      performanceMonitor.end('Navigate to featured content');
    } else {
      console.error('Could not find featured item with id:', id, 'or item has no slug');
      toast.error('Featured content not found', {
        description: 'The requested featured content could not be located.'
      });
    }
  }, [featuredContent, navigate]);
  
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    performanceMonitor.start('Content refresh');
    setIsRefreshing(true);
    
    toast.info("Refreshing content...", {
      description: "Getting the latest travel insights for you"
    });
    
    try {
      await Promise.all([
        featuredContent.refreshContent(), 
        regularContent.refreshContent()
      ]);
      
      const duration = performanceMonitor.end('Content refresh');
      toast.success("Content refreshed successfully", {
        description: `Updated in ${(duration / 1000).toFixed(1)}s`
      });
    } catch (error) {
      console.error("Error refreshing content:", error);
      toast.error("Failed to refresh content", {
        description: "Please check your connection and try again."
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [featuredContent, regularContent, isRefreshing]);
  
  const handleTabChange = useCallback((value: string) => {
    performanceMonitor.start(`Tab change to ${value}`);
    console.log('Tab changed to:', value);
    setActiveTab(value);
    setCurrentPage(1);
    // Clear content type filter when changing tabs
    setFilters(prev => ({ ...prev, contentType: undefined }));
    performanceMonitor.end(`Tab change to ${value}`);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    performanceMonitor.start('Page navigation');
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    performanceMonitor.end('Page navigation');
  }, []);

  const handleSearch = useCallback((searchQuery: string) => {
    console.log('Search query:', searchQuery);
    setFilters(prev => ({ ...prev, search: searchQuery || undefined }));
    setCurrentPage(1);
  }, []);

  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    console.log('Filters changed:', newFilters);
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  // Use effect to trigger fetch when tab or page changes
  useEffect(() => {
    regularContent.fetchShortContent(regularOptions);
  }, [activeTab, currentPage, filters.category, filters.contentType]);
  
  // Responsive column calculation based on screen size
  const getResponsiveColumns = useCallback((): { featured: 1 | 2 | 3 | 4; regular: 1 | 2 | 3 | 4 } => {
    if (isMobile) {
      return { featured: 1, regular: 1 }; // Mobile: 1 column
    }
    
    // Use CSS breakpoint detection for more accurate responsive behavior
    const width = window.innerWidth;
    if (width < 640) return { featured: 1, regular: 1 }; // Mobile
    if (width < 768) return { featured: 2, regular: 2 }; // Small tablet
    if (width < 1024) return { featured: 2, regular: 2 }; // Tablet
    if (width < 1280) return { featured: 3, regular: 3 }; // Desktop
    return { featured: 3, regular: 4 }; // Large desktop
  }, [isMobile]);

  const responsiveColumns = useMemo(() => getResponsiveColumns(), [getResponsiveColumns]);

  const hasError = featuredContent.error || regularContent.error;
  const isLoading = featuredContent.isLoading || regularContent.isLoading;
  const hasContent = featuredContent.items.length > 0 || regularContent.items.length > 0;
  
  return (
    <div className="container py-8">
      <Helmet>
        <title>Travel Bytes | Quick Travel Insights</title>
        <meta name="description" content="Discover quick travel insights, tips, and interesting facts about destinations around the world." />
        <meta name="keywords" content="travel tips, travel facts, travel quotes, travel insights, destination guide" />
        <link rel="canonical" href={`${baseUrl}/travel-bytes`} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Travel Bytes | Quick Travel Insights" />
        <meta property="og:description" content="Discover quick travel insights, tips, and interesting facts about destinations around the world." />
        <meta property="og:url" content={`${baseUrl}/travel-bytes`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${baseUrl}/og-image.png`} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Travel Bytes | Quick Travel Insights" />
        <meta name="twitter:description" content="Discover quick travel insights, tips, and interesting facts about destinations around the world." />
        <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />
      </Helmet>
      
      {/* Breadcrumbs */}
      <TravelByteBreadcrumbs baseUrl={baseUrl} />
      
      {/* Loading progress bar */}
      {loadingProgress > 0 && loadingProgress < 100 && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <Progress value={loadingProgress} className="h-1 rounded-none" />
        </div>
      )}
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold">Travel Bytes</h1>
          <p className="text-muted-foreground mt-1">
            Quick travel insights, tips, and interesting facts
          </p>
          {hasContent && (
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              <span>{featuredContent.items.length + regularContent.items.length} insights available</span>
            </div>
          )}
        </div>
        
        <div className="mt-4 md:mt-0 flex space-x-2 animate-fade-in">
          <Button 
            variant="outline" 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            className="relative"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Content'}
          </Button>
        </div>
      </div>

      {/* Search and Filters Section */}
      <div className="mb-8 space-y-4 animate-fade-in">
        <TravelByteSearch 
          onSearch={handleSearch}
          initialValue={filters.search || ""}
        />
        
        <TravelByteFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          categories={availableCategories}
          contentTypes={contentTypes}
        />
      </div>
      
      {/* Enhanced error display */}
      {hasError && (
        <Alert variant="destructive" className="mb-6 animate-fade-in">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to load content</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{featuredContent.error?.message || regularContent.error?.message || "An unexpected error occurred while loading travel content."}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                Try Again
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Enhanced loading indicator */}
      {(isLoading || isRefreshing) && !hasError && (
        <Alert className="mb-6 animate-fade-in">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <AlertTitle>Loading travel insights</AlertTitle>
          <AlertDescription>
            Fetching the latest travel tips and insights for you...
          </AlertDescription>
        </Alert>
      )}
      
      {/* Featured content section */}
      {featuredContent.items && featuredContent.items.length > 0 && (
        <div className="mb-12 animate-fade-in">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Featured Content ({featuredContent.items.length})
          </h2>
          <ShortContentGrid 
            items={featuredContent.items} 
            isLoading={featuredContent.isLoading}
            onItemView={handleViewFeatured}
            onRefresh={handleRefresh}
            error={featuredContent.error}
            columns={responsiveColumns.featured}
          />
        </div>
      )}
      
      {/* Main content tabs */}
      <Tabs defaultValue="all" value={activeTab} onValueChange={handleTabChange} className="mb-6">
        <TabsList className="w-full sm:w-auto flex flex-wrap justify-start overflow-x-auto">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="tip" className="flex items-center">
            <Lightbulb className="h-4 w-4 mr-1" /> Tips
          </TabsTrigger>
          <TabsTrigger value="fact" className="flex items-center">
            <Info className="h-4 w-4 mr-1" /> Facts
          </TabsTrigger>
          <TabsTrigger value="quote" className="flex items-center">
            <Quote className="h-4 w-4 mr-1" /> Quotes
          </TabsTrigger>
          <TabsTrigger value="highlight" className="flex items-center">
            <Star className="h-4 w-4 mr-1" /> Highlights
          </TabsTrigger>
          <TabsTrigger value="snippet" className="flex items-center">
            <FileText className="h-4 w-4 mr-1" /> Snippets
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-6">
          <ShortContentGrid 
            items={filteredAndSortedItems} 
            isLoading={regularContent.isLoading || isRefreshing}
            onItemView={handleViewItem}
            onRefresh={handleRefresh}
            error={regularContent.error}
            columns={responsiveColumns.regular}
          />
          
          {/* Enhanced Pagination */}
          {regularContent.totalPages > 1 && (
            <div className="mt-8 animate-fade-in">
              <TravelBytePagination
                currentPage={currentPage}
                totalPages={regularContent.totalPages}
                onPageChange={handlePageChange}
                isLoading={regularContent.isLoading}
                totalItems={regularContent.totalCount}
                itemsPerPage={12}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
